import { openDB, IDBPDatabase } from 'idb'
import { useState, useEffect, useCallback } from 'react'

const DB_NAME = 'image-prompt-tester'
const DB_VERSION = 1
const STORE_NAME = 'test-images'
const MAX_IMAGE_SIZE = 4 * 1024 * 1024 // 4MB

export interface StoredImage {
    id: string
    name: string
    mimeType: string
    blob: Blob
    target: 'Yes' | 'No' | null
    createdAt: number
}

export interface ImageStore {
    images: StoredImage[]
    loading: boolean
    saveImages: (files: File[]) => Promise<void>
    deleteImage: (id: string) => Promise<void>
    updateTarget: (id: string, target: 'Yes' | 'No' | null) => Promise<void>
    clearAll: () => Promise<void>
}

function generateId(): string {
    return crypto.randomUUID()
}

async function getDb(): Promise<IDBPDatabase> {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' })
            }
        },
    })
}

export function useImageStore(): ImageStore {
    const [images, setImages] = useState<StoredImage[]>([])
    const [loading, setLoading] = useState(true)

    // Load images on mount
    useEffect(() => {
        async function load() {
            const db = await getDb()
            const all = await db.getAll(STORE_NAME)
            setImages(all.sort((a, b) => a.createdAt - b.createdAt))
            setLoading(false)
        }
        load()
    }, [])

    const saveImages = useCallback(async (files: File[]) => {
        const db = await getDb()
        const newImages: StoredImage[] = []

        for (const file of files) {
            if (file.size > MAX_IMAGE_SIZE) {
                throw new Error(`Image "${file.name}" exceeds 4MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB)`)
            }

            const stored: StoredImage = {
                id: generateId(),
                name: file.name,
                mimeType: file.type,
                blob: file,
                target: null,
                createdAt: Date.now(),
            }
            await db.put(STORE_NAME, stored)
            newImages.push(stored)
        }

        setImages((prev) => [...prev, ...newImages])
    }, [])

    const deleteImage = useCallback(async (id: string) => {
        const db = await getDb()
        await db.delete(STORE_NAME, id)
        setImages((prev) => prev.filter((img) => img.id !== id))
    }, [])

    const updateTarget = useCallback(async (id: string, target: 'Yes' | 'No' | null) => {
        const db = await getDb()
        const existing = await db.get(STORE_NAME, id)
        if (existing) {
            existing.target = target
            await db.put(STORE_NAME, existing)
            setImages((prev) => prev.map((img) => (img.id === id ? { ...img, target } : img)))
        }
    }, [])

    const clearAll = useCallback(async () => {
        const db = await getDb()
        await db.clear(STORE_NAME)
        setImages([])
    }, [])

    return { images, loading, saveImages, deleteImage, updateTarget, clearAll }
}
